<!-- ============================================================================
     ACTIVE SUBSCRIPTION PLAN - PRICING SUMMARY
     Matches the provided design exactly
     ============================================================================ -->

<!-- Inactive Subscription Alert -->
<div class="inactive-subscription-alert" *ngIf="isSubscriptionInactive()">
  <div class="alert-icon">
    <app-icon name="alert-circle" width="24" height="24"></app-icon>
  </div>
  <div class="alert-content">
    <div class="alert-title">
      {{
        subscriptionStatus === SubscriptionStatus.Cancelled
          ? 'Subscription Cancelled'
          : subscriptionStatus === SubscriptionStatus.Suspended
            ? 'Subscription Suspended'
            : 'Subscription Expired'
      }}
    </div>
    <div class="alert-message">{{ getInactiveSubscriptionMessage() }}</div>
  </div>
  <button class="alert-action-btn" (click)="onChangePlanClick()" *ngIf="allowPlanChange">
    <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
      <path
        d="M5 12h14M12 5l7 7-7 7"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    Select a Plan
  </button>
</div>

<div class="pricing-page" *ngIf="activeSubscriptionPlan">
  <!-- PLAN BANNER -->
  <div class="plan-banner">
    <div class="plan-banner-left">
      <div class="plan-label">{{ planLabel }}</div>
      <div class="plan-name">
        {{ activeSubscriptionPlan.name }}
        <span
          class="status-badge"
          *ngIf="showStatusBadge"
          [class.status-active]="subscriptionStatus === SubscriptionStatus.Active"
          [class.status-trial]="subscriptionStatus === SubscriptionStatus.Trial"
          [class.status-pastdue]="subscriptionStatus === SubscriptionStatus.PastDue"
          [class.status-suspended]="subscriptionStatus === SubscriptionStatus.Suspended"
          [class.status-expired]="subscriptionStatus === SubscriptionStatus.Expired"
          [class.status-cancelled]="subscriptionStatus === SubscriptionStatus.Cancelled"
        >
          {{ getStatusLabel() }}
        </span>
        <span class="meta-chip meta-chip-green">
          {{ getBillingCycleDisplayName() }}
        </span>
      </div>
      <div class="plan-meta">
        <span class="meta-chip" *ngIf="activeSubscriptionPlan.description">{{
          activeSubscriptionPlan.description
        }}</span>
      </div>
      <!-- Trial Info -->
      <div
        class="trial-info-block"
        *ngIf="subscriptionStatus === SubscriptionStatus.Trial && trialEndDate"
      >
        <span class="trial-end-date">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="3"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M16 2v4M8 2v4M3 10h18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          Trial ends: {{ getTrialEndDateFormatted() }}
        </span>
        <span class="trial-countdown" *ngIf="getTrialRemainingDaysLabel()">
          {{ getTrialRemainingDaysLabel() }}
        </span>
      </div>
      <!-- Scheduled Start Date -->
      <div class="scheduled-start-info" *ngIf="scheduledStartDate">
        <span class="scheduled-start-chip">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="3"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M16 2v4M8 2v4M3 10h18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          Effective from: <strong>{{ scheduledStartDate | date: 'mediumDate' }}</strong>
        </span>
      </div>
    </div>
    <div class="plan-banner-right">
      <div>
        <span class="plan-price-amount">{{ calculateTotalSubscriptionPrice() | currency }}</span>
        <span class="plan-price-period">{{ getBillingPeriodLabel() }}</span>
      </div>
      <div class="plan-save-badge" *ngIf="calculateDiscountSavings() as savings">
        Saving
        {{ savings.isPercentage ? savings.savedValue + '%' : (savings.savedAmount | currency) }}
        this cycle
      </div>
    </div>
  </div>

  <!-- MAIN VERTICAL LAYOUT -->
  <div class="layout">
    <!-- SCHEDULED PLAN NOTICE -->
    <div
      class="scheduled-plan-notice"
      *ngIf="hasScheduledPlan"
      [class.clickable]="true"
      (click)="viewScheduledPlanRequested.emit()"
      [attr.role]="'button'"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
      <ng-container *ngIf="!isAddOnUpdateScheduled">
        <span>An upcoming subscription has been scheduled for this company.</span>
        <span class="scheduled-plan-notice-link">Click here to view the details.</span>
        <svg
          class="ms-auto"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M9 18l6-6-6-6"></path>
        </svg>
      </ng-container>
      <ng-container *ngIf="isAddOnUpdateScheduled">
        <span>An update to the add-ons has been scheduled for this company.</span>
        <span class="scheduled-plan-notice-link">Click here to view the details.</span>
        <svg
          class="ms-auto"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M9 18l6-6-6-6"></path>
        </svg>
      </ng-container>
    </div>

    <!-- PENDING CANCELLATION BANNER -->
    <div class="pending-cancellation-banner" *ngIf="isCancellationScheduled && cancellationDate">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span
        >The current subscription is scheduled for cancellation and will automatically be canceled
        on <strong>{{ cancellationDate | date: 'MMMM d, y' }}</strong
        >.</span
      >
    </div>

    <!-- APPS SECTION -->
    <div class="apps-section">
      <div class="col-label">Apps Included in Plan</div>

      <div class="apps-grid">
        <!-- App Card for each app -->
        <div
          class="app-card"
          *ngFor="let app of activeSubscriptionPlan.apps; trackBy: trackByApplicationId"
        >
          <div class="app-card-head">
            <div class="app-icon" style="background: #fff3eb">
              <img
                [src]="getApplicationIcon(app.appId)"
                [alt]="app.appName"
                style="width: 40px; height: 35px; object-fit: contain"
              />
            </div>
            <div class="app-head-text">
              <div class="app-head-name">{{ app.appName }} Access</div>
              <div class="app-head-id">
                {{ app.appName.toLowerCase().replace(' ', '-') }}-access
              </div>
            </div>
            <div class="app-head-total">
              <div class="app-total-amount">
                {{
                  app.basePrice + (app.currentSeats - app.includedSeats) * app.perSeatPrice
                    | currency
                }}
              </div>
              <div class="app-total-label">{{ getBillingPeriodLabel() }}</div>
            </div>
          </div>
          <div class="app-card-body">
            <div class="app-stat">
              <div class="app-stat-label">Base Price</div>
              <div class="app-stat-value">{{ app.basePrice | currency }}</div>
              <div class="app-stat-sub">flat{{ getBillingPeriodLabel() }}</div>
            </div>
            <div class="app-stat" *ngIf="hasPerSeatCharge(app) === false">
              <div class="app-stat-label">Seats: NA</div>
            </div>
            <div class="app-stat" *ngIf="hasPerSeatCharge(app)">
              <div class="app-stat-label">Seats</div>
              <div class="seats-visual">
                <span class="seat-pill seat-pill-incl">{{ app.includedSeats }} incl.</span>
                <span class="seat-plus" *ngIf="app.currentSeats > app.includedSeats">+</span>
                <span
                  class="seat-pill seat-pill-extra"
                  *ngIf="app.currentSeats > app.includedSeats"
                >
                  {{ app.currentSeats - app.includedSeats }} extra
                </span>
              </div>
            </div>
            <div class="app-stat">
              <!-- Show revenue sharing for apps like Lister -->
              <ng-container *ngIf="isRevenueSharing(app)">
                <div class="app-stat-label">Revenue Sharing</div>
                <div class="app-stat-value">{{ getRevenueSharingPercentage(app) }}%</div>
                <div class="app-stat-sub">of revenue</div>
              </ng-container>
              <!-- Show per-seat pricing for other apps -->
              <ng-container *ngIf="!isRevenueSharing(app)">
                <div class="app-stat-label">Per Extra Seat</div>
                <div class="app-stat-value" *ngIf="app.perSeatPrice > 0">
                  {{ app.perSeatPrice | currency }}
                </div>
                <span class="free-tag" *ngIf="app.perSeatPrice === 0">$0.00</span>
                <div class="app-stat-sub">
                  {{ app.perSeatPrice > 0 ? '/seat' + getBillingPeriodLabel() : 'no extra charge' }}
                </div>
              </ng-container>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ADD-ONS SECTION -->
    <div
      class="addons-section"
      *ngIf="activeSubscriptionPlan.addons && activeSubscriptionPlan.addons.length > 0"
    >
      <div class="col-label">Active Add-ons</div>

      <div class="addons-grid">
        <!-- Addon Card for each addon -->
        <div
          class="addon-card"
          *ngFor="let addon of activeSubscriptionPlan.addons; trackBy: trackByAddonId"
        >
          <div class="addon-card-header">
            <div class="addon-dot" style="background: #d97706"></div>
            <div class="addon-card-title">{{ addon.name }}</div>
          </div>
          <div class="row">
            <div class="col-md-6" *ngFor="let charge of getAddonAllChargesForDisplay(addon)">
              <span class="addon-amount">{{ charge.value }}</span>
              <span class="addon-period">{{ charge.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SUMMARY SECTION -->
    <div class="summary-section">
      <div class="col-label">Price Summary</div>

      <div class="summary-card">
        <!-- Detailed Charges for Each App -->
        <ng-container
          *ngFor="
            let appCharges of getDetailedChargeBreakdownByApplication();
            trackBy: trackByAppCharges
          "
        >
          <ng-container *ngFor="let charge of appCharges.charges">
            <div class="summary-row" [style.opacity]="charge.includedInTotal ? '1' : '0.6'">
              <div class="sum-label-wrapper">
                <div class="sum-label">
                  {{ appCharges.appName }} Access
                  <span class="not-included-badge" *ngIf="!charge.includedInTotal">At Actual</span>
                </div>
                <div class="sum-sublabel">
                  {{ charge.typeName }} {{ formatChargeValue(charge.value, charge.type) }}
                </div>
              </div>
              <div
                class="sum-value"
                [style.text-decoration]="charge.includedInTotal ? 'none' : 'line-through'"
              >
                {{
                  charge.includedInTotal
                    ? formatChargeValue(charge.value, charge.type)
                    : (0 | currency)
                }}
              </div>
            </div>
          </ng-container>
        </ng-container>

        <!-- Detailed Charges for Each Addon -->
        <ng-container
          *ngFor="
            let addonCharges of getDetailedChargeBreakdownByAddon();
            trackBy: trackByAddonCharges
          "
        >
          <ng-container *ngFor="let charge of addonCharges.charges">
            <div class="summary-row" [style.opacity]="charge.includedInTotal ? '1' : '0.6'">
              <div class="sum-label-wrapper">
                <div class="sum-label">
                  {{ addonCharges.addonName }} (Add-on)
                  <span class="not-included-badge" *ngIf="!charge.includedInTotal">At Actual</span>
                </div>
                <div class="sum-sublabel">
                  {{ charge.typeName }} {{ formatChargeValue(charge.value, charge.type) }}
                </div>
              </div>
              <div
                class="sum-value"
                [style.text-decoration]="charge.includedInTotal ? 'none' : 'line-through'"
              >
                {{
                  charge.includedInTotal
                    ? formatChargeValue(charge.value, charge.type)
                    : (0 | currency)
                }}
              </div>
            </div>
          </ng-container>
        </ng-container>

        <hr class="sum-divider" />

        <div class="summary-row">
          <div class="sum-label">Subtotal</div>
          <div class="sum-value sum-value-muted">{{ calculateSubtotal() | currency }}</div>
        </div>

        <div class="discount-row-s" *ngIf="calculateDiscountSavings() as savings">
          <div>
            <div class="disc-label">
              Discount
              <span class="disc-badge">
                {{
                  savings.isPercentage
                    ? savings.savedValue + '% OFF'
                    : (savings.savedAmount | currency) + ' OFF'
                }}
              </span>
            </div>
            <div class="disc-sub">Applied to this cycle</div>
          </div>
          <div class="disc-value">−{{ savings.savedAmount | currency }}</div>
        </div>

        <div class="total-row">
          <div>
            <div class="total-label">Total Price</div>
          </div>
          <div style="text-align: right">
            <div class="total-amount">{{ calculateTotalSubscriptionPrice() | currency }}</div>
            <div class="total-period">
              {{ getBillingPeriodLabel() === '/mo' ? 'per month' : 'per year' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ACTION BUTTONS -->
  <div class="action-row" *ngIf="showActionButtons">
    <button
      class="btn btn-primary"
      *ngIf="canShowChangePlanButton() && canShowCancelButton()"
      (click)="onChangePlanClick()"
    >
      <app-icon
        name="edit"
        width="14"
        height="14"
        [attr.stroke]="'#fff'"
        strokeWidth="2"
      ></app-icon>
      {{ changePlanButtonText }}
    </button>
    <button
      class="btn btn-primary"
      *ngIf="canShowManageAddOnsButton()"
      (click)="onManageAddOnsClick()"
    >
      <app-icon name="plus" width="14" height="14" strokeWidth="2"></app-icon>
      Manage Add-ons
    </button>
    <button
      class="btn btn-danger"
      *ngIf="canShowCancelButton()"
      (click)="onCancelPlanClick()"
      [disabled]="isCancellingInProgress"
    >
      <app-icon name="delete" width="14" height="14"></app-icon>
      {{ isCancellingInProgress ? 'Cancelling...' : cancelButtonText }}
    </button>
  </div>
</div>

<!-- No Active Subscription State -->
<div class="no-subscription-state" *ngIf="!activeSubscriptionPlan && !isLoading">
  <app-icon
    name="image"
    width="64"
    height="64"
    [attr.stroke]="'#94a3b8'"
    strokeWidth="1.5"
  ></app-icon>
  <h3>No Active Subscription</h3>
  <p>Please select a plan below to get started with Brijjworks apps and services.</p>
  <button class="btn btn-primary" *ngIf="allowPlanSelection" (click)="onChangePlanClick()">
    <app-icon name="grid" width="14" height="14" [attr.stroke]="'#fff'" strokeWidth="2"></app-icon>
    Select a Plan
  </button>
</div>

<!-- Loading State -->
<div class="subscription-loading-state" *ngIf="isLoading">
  <div class="spinner-border text-primary" role="status"></div>
  <span class="loading-text">Loading subscription details...</span>
</div>
